import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  ShowChart as TrendIcon,
  Notifications as AlertIcon,
  Assignment as InspectionIcon,
  DeviceHub as DeviceIcon,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '@hooks/redux'
import { fetchDashboard } from '@store/slices/dashboardsSlice'

const widgetTarget = (widgetType: string) => {
  if (widgetType.includes('alert')) return { path: '/alerts', label: 'Open alerts', icon: <AlertIcon /> }
  if (widgetType.includes('inspection')) return { path: '/inspections', label: 'Open inspections', icon: <InspectionIcon /> }
  if (widgetType.includes('device')) return { path: '/devices', label: 'Open devices', icon: <DeviceIcon /> }
  if (widgetType.includes('chart') || widgetType.includes('metric') || widgetType.includes('weather')) {
    return { path: '/measurements', label: 'Open trends', icon: <TrendIcon /> }
  }
  return { path: '/', label: 'Open hives', icon: <TrendIcon /> }
}

const DashboardView: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { selectedDashboard, loadingState, error } = useAppSelector((state) => state.dashboards)

  useEffect(() => {
    if (id) {
      dispatch(fetchDashboard(parseInt(id, 10)))
    }
  }, [dispatch, id])

  if (loadingState === 'pending' && !selectedDashboard) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  if (!selectedDashboard) {
    return (
      <Container sx={{ py: 3 }}>
        <Alert severity="error">{error || 'Dashboard not found.'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/dashboards')}>
          Back to dashboards
        </Button>
      </Container>
    )
  }

  const widgets = selectedDashboard.widgets || []

  return (
    <Container sx={{ py: 3 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/dashboards')} sx={{ mb: 2 }}>
        All dashboards
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {selectedDashboard.name}
          </Typography>
          <Typography color="text.secondary">
            {selectedDashboard.description || 'Live hive overview'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/dashboards/${selectedDashboard.id}/edit`)}
        >
          Edit dashboard
        </Button>
      </Box>

      <Grid container spacing={2}>
        {widgets.length === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">This dashboard has no widgets yet. Edit it to add hives and layout.</Alert>
          </Grid>
        ) : (
          widgets.map((widget) => {
            const target = widgetTarget(widget.widget_type)
            return (
              <Grid item xs={12} sm={6} md={4} key={widget.id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Chip size="small" label={widget.widget_type.replace(/_/g, ' ')} sx={{ mb: 1 }} />
                    <Typography variant="h6">{widget.name}</Typography>
                    <Button
                      sx={{ mt: 2 }}
                      startIcon={target.icon}
                      onClick={() => navigate(target.path)}
                    >
                      {target.label}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )
          })
        )}
      </Grid>
    </Container>
  )
}

export default DashboardView
